import { useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import { AuthenticatedTemplate } from "@azure/msal-react";
import WelcomeSection from "../../../components/templates/DashboardTeamplate/WelcomeSection/WelcomeSection";
import Highlights from "../../../components/templates/DashboardTeamplate/HighlightsSection/Highlights";
import RecentLeads from "../../../components/templates/DashboardTeamplate/RecentLeads/RecentLeads";
import { ICard } from "../models/DashboardModels";
import { Link } from "react-router-dom";
import useFetchLeads from "../../../hooks/usefetchLeads";
import { Col, Form, Row } from "react-bootstrap";
import LoaderHome from "../../../components/atoms/Loader-home/Loader-home";
import { ChartOptionsMap, ChartType } from "./constants";
import OperatorPerformance from "../../../components/molecules/OperatorPerformanceWidget/OperatorPerformance";
import useUserRole from "../../../hooks/useUserRole";
import PageTitle from "../../../components/organisms/Headline/PageTitle";
import IconHome from "../../../styles/icons/IconHome";
import { UserRole } from "../../../utils";

type PeriodView = "date" | "month" | "year";

const toView = (activeChart: string): PeriodView => {
  switch (activeChart) {
    case "Month":
      return "month";
    case "Year":
      return "year";
    case "Date":
    default:
      return "date";
  }
};

const DashboardLayout = () => {
  const {
    allLeads,
    hotLeads,
    warmLeads,
    interestedLeads,
    leadCount,
    status,
    fetchLeads,
  } = useFetchLeads();

  const [activeChart, setActiveChart] = useState<string>("Date");
  const [chartType, setChartType] = useState<ChartType>(ChartType.Area);

  const totalLeads = allLeads?.totalRecords ?? 0;
  const highIntentCount = hotLeads?.totalRecords ?? 0;
  const mediumIntentCount = warmLeads?.totalRecords ?? 0;
  const lowIntentCount = interestedLeads?.totalRecords ?? 0;

  const getPercentage = (value: number, total: number) => {
    if (!total) return "0.00%";
    return `${((value / total) * 100).toFixed(2)}%`;
  };

  const highIntentRate = getPercentage(highIntentCount, totalLeads);
  const lowIntentRate = getPercentage(lowIntentCount, totalLeads);

  const cards = useMemo(
    () => [
      {
        title: "Total Leads",
        subTitle: "Tracking all leads",
        count: totalLeads,
        className: "item all",
        icon: "allLeads",
        link: "/leads/all-leads",
      },
      {
        title: "High Intent",
        subTitle: "AI classified",
        count: highIntentCount,
        className: "item hot",
        icon: "hotLeads",
        link: "/leads/hot-leads",
      },
      {
        title: "Medium Intent",
        subTitle: "AI classified",
        count: mediumIntentCount,
        className: "item warm",
        icon: "warmLeads",
        link: "/leads/warm-leads",
      },
      {
        title: "Low Intent",
        subTitle: "AI classified",
        count: lowIntentCount,
        className: "item interested",
        icon: "interestedLeads",
        link: "/leads/interested-leads",
      },
      {
        title: "High Intent Rate",
        subTitle: "Confidence-based",
        count: highIntentRate,
        className: "item high-intent-rate",
        icon: "highIntentRateIcon",
        link: "/leads/hot-leads",
      },
      {
        title: "Low Intent %",
        subTitle: "AI classified",
        count: lowIntentRate,
        className: "item low-intent-rate",
        icon: "lowIntentPercentageIcon",
        link: "/leads/interested-leads",
      },
    ],
    [
      totalLeads,
      highIntentCount,
      mediumIntentCount,
      lowIntentCount,
      highIntentRate,
      lowIntentRate,
    ]
  );

  const chartItems = useMemo(
    () =>
      ["Date", "Month", "Year"].map((item) => ({
        item,
        isActive: item === activeChart,
      })),
    [activeChart]
  );

  const handleChartClick = (type: string) => {
    setActiveChart(type);
    fetchLeads(type);
  };

  const SelectedChartComponent = ChartOptionsMap[chartType].component;
  const view = toView(activeChart);
  const userRole = useUserRole();

  return (
    <AuthenticatedTemplate>
      <Container fluid className="layout-bar dashboard">
        <Row>
          <Col sm={12}>
            <WelcomeSection />
          </Col>
        </Row>

        <Row>
          <Col sm={12}>
            {status === "loading" ? (
              <div className="loader-wrapper">
                <LoaderHome />
              </div>
            ) : (
              <Highlights cardsData={cards as ICard[]} />
            )}
          </Col>
        </Row>

        <Row className="left-section">
          <Col sm={12} md={6} lg={6} className="no-padding-mobile">
            {status === "loading" ? (
              <div className="loader-wrapper">
                <LoaderHome />
              </div>
            ) : (
              <div>
                <RecentLeads
                  allLeads={
                    userRole === UserRole.Administrator
                      ? allLeads?.data.slice(0, 5)
                      : allLeads?.data.slice(0, 3)
                  }
                  loading={status}
                />
              </div>
            )}
          </Col>

          <Col sm={12} md={6} lg={6} className="no-padding-mobile">
            {status === "loading" ? (
              <div className="loader-wrapper">
                <LoaderHome />
              </div>
            ) : (
              <div className="main-content">
                {userRole !== "Customer" && userRole !== "Operator" && (
                  <div className="operator-performance-wrapper">
                    <PageTitle title="Operator Performance" subTitle="" />
                    <OperatorPerformance
                      isHotWarmPercentage={false}
                      isHotCounter={false}
                      isShowView={true}
                      isInterestedCounter={false}
                      isRejectedCounter={false}
                      isWarmCounter={false}
                      isShowViewAll={true}
                      pageSize={3}
                    />
                  </div>
                )}

                <div className="line-chart-wrapper no-mobile">
                  <div className="title">
                    Lead flow <br />
                    <p>Volume tracking across your acquisition channels</p>
                  </div>

                  <div className="line-chart-content">
                    <div className="links-wrapper">
                      <div>
                        <Form.Select
                          value={chartType}
                          onChange={(e) =>
                            setChartType(e.target.value as ChartType)
                          }
                        >
                          {Object.entries(ChartOptionsMap).map(
                            ([key, { label }]) => (
                              <option key={key} value={key}>
                                {label}
                              </option>
                            )
                          )}
                        </Form.Select>
                      </div>

                      <div className="filter-links">
                        {chartItems.map((nav: any, index: number) => (
                          <Link
                            key={index}
                            onClick={() => handleChartClick(nav.item)}
                            className={nav.isActive ? "active" : ""}
                            to=""
                          >
                            {nav.item}
                          </Link>
                        ))}
                      </div>
                    </div>

                    <div className="line-chart">
                      <SelectedChartComponent
                        leadCount={leadCount}
                        status={status}
                        view={view}
                      />
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <IconHome breadcrumb="infoIcon" />
                      <p>
                        All metrics are updated in real-time. Data accuracy is
                        Al-driven!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </AuthenticatedTemplate>
  );
};

export default DashboardLayout;
